import React from 'react';
import { sharePostcard, postcardStatus } from '../../utils/eventPostcard';

/**
 * 🎬📝 THE CREDITS CARD (09-23, MOVIE_NIGHT_UX_HANDOFF §3.7, Nina's ruling 16):
 * at the wrap, anyone who was in the event room or seated gets ONE card in the
 * feed dock (never a modal; the after-film chatter flows around it): three
 * taps + an optional line, Skip / Send, once per device. Modeled on
 * GenreVoteCard: pure, in flow, border-box, touch-action manipulation, ✕ =
 * "Not now" (hides for this visit only).
 *
 * `feedback` = the status rider `house.feedback`: { event_id, template, label,
 * room, phase, questions: [{ key, q, options: [label, ...] }] }.
 * `answers` = { [key]: optionIndex }. `state` ∈ 'idle' | 'sending' | 'thanks'.
 * Every action is a callback; the page owns the answers, the text, the POST
 * (utils/eventFeedback.submitEventFeedback) and the once-gate.
 * data-event-feedback / data-feedback-q / data-feedback-opt = grep markers.
 * ⚠️ No global box-sizing here · the input is 16px (no iOS zoom).
 */
export const FEEDBACK_TEXT_MAX = 1000;
export const FEEDBACK_THANKS = 'Got it. Thank you!';
export const FEEDBACK_PLACEHOLDER = 'Anything else? (optional)';

const isAnswered = (answers, q) => !!q && Number.isInteger(answers[q.key]) && answers[q.key] >= 0;
const OPT_CSS = '.event-fb-opt{touch-action:manipulation;-webkit-tap-highlight-color:transparent;transition:border-color 0.12s ease, background 0.12s ease, transform 0.08s ease} .event-fb-opt:active{transform:scale(0.985)}';

/** 🗓️ 10-02: "Sun, Oct 4, 10 AM" in the viewer's own clock (an `at`), or "Sun, Oct 4" (a `day`). */
export function nextWhen(next) {
  try {
    if (next && next.at) {
      const d = new Date(next.at);
      if (Number.isFinite(d.getTime())) return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric' });
    }
    if (next && /^\d{4}-\d{2}-\d{2}$/.test(String(next.day))) return new Date(`${next.day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch (e) { /* no date beats a wrong one */ }
  return '';
}

/**
 * 📸 10-02 THE POSTCARD (Nina: a shareable artifact on the card "that can help us advertise claude house"):
 * when the rider carries `photo_url` (the event's group photo, our bucket only, checked server-side), the card
 * shows it centered (Nina's layout pass: "a little bigger and centered, with the share button under it"; capped
 * at 300px so the dock never swallows a phone) + "Share the photo" (utils/eventPostcard: the phone's share
 * sheet WITH the picture, else download + the house link copied). Its own tiny state, so the card stays pure;
 * no photo = no block (every other card byte-identical).
 */
function PostcardBlock({ url, label, accent, ink, muted }) {
  const [state, setState] = React.useState('idle'); // 'idle' | 'making' | a sharePostcard result
  const busy = state === 'making';
  const onShare = async () => {
    if (busy) return;
    setState('making');
    let result = 'failed';
    try { result = await sharePostcard({ photoUrl: url, label }); } catch (e) { result = 'failed'; }
    setState(result === 'cancelled' ? 'idle' : result);
  };
  const status = postcardStatus(state);
  return (
    <div data-feedback-postcard="1" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, margin: '8px 0 12px' }}>
      <a href={url} target="_blank" rel="noopener noreferrer" aria-label="Open tonight's photo" style={{ display: 'block', width: '100%', maxWidth: 300, lineHeight: 0 }}>
        <img src={url} alt="Everyone at tonight's event" loading="lazy" style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8, border: `1px solid ${accent}55`, display: 'block', boxSizing: 'border-box' }} />
      </a>
      <button type="button" className="event-fb-opt" data-feedback-share="1" onClick={onShare} disabled={busy}
        style={{ font: 'inherit', fontSize: '0.82em', fontWeight: 800, padding: '6px 16px', borderRadius: 999, cursor: busy ? 'default' : 'pointer', boxSizing: 'border-box', background: `${accent}33`, border: `1px solid ${accent}`, color: ink }}>
        {busy ? 'Making it…' : 'Share the photo'}
      </button>
      {status && <span data-feedback-share-status="1" style={{ fontSize: '0.78em', color: muted, overflowWrap: 'anywhere', textAlign: 'center' }}>{status}</span>}
    </div>
  );
}

export default function EventFeedbackCard({ feedback, answers = {}, onAnswer, text = '', onText, onSend, onSkip, onDismiss, state = 'idle', THEME = {} }) {
  if (!feedback) return null;
  // 📸 10-06 (Nina: "the review should happen AFTER the event not 30 minutes before. The group photo is 30 minutes
  // before the event end"): the card renders on its questions OR the photo; no questions = the photo alone.
  const questions = Array.isArray(feedback.questions) ? feedback.questions.filter((q) => q && q.key && Array.isArray(q.options) && q.options.length) : [];
  const photoOnly = !questions.length;
  if (photoOnly && !(typeof feedback.photo_url === 'string' && /^https:\/\//.test(feedback.photo_url))) return null;
  const T = THEME || {};
  const accent = T.accent || '#7b1fa2';
  const ink = T.text || '#eee';
  const muted = T.textMuted || '#999';
  const a = answers || {};
  const answered = questions.some((q) => isAnswered(a, q));
  const hasText = !!String(text || '').trim();
  const sending = state === 'sending';
  const canSend = !sending && (answered || hasText);

  const root = {
    position: 'relative', margin: '0 0 8px', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box', maxWidth: '100%',
    background: T.bgLight || 'rgba(20,16,30,0.92)', border: `1px solid ${accent}55`, boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
    color: ink,
  };

  // 📸 10-02: the postcard row, only when the rider carries the event's photo.
  const photo = typeof feedback.photo_url === 'string' && /^https:\/\//.test(feedback.photo_url) ? feedback.photo_url : null;
  const postcard = photo ? <PostcardBlock url={photo} label={feedback.label} accent={accent} ink={ink} muted={muted} /> : null;

  // 🗓️ 10-02 (Nina): the night in one line, and after Send the next event + a fun line.
  const summary = typeof feedback.summary === 'string' && feedback.summary.trim() ? feedback.summary.trim() : null;
  const next = feedback.next && typeof feedback.next.label === 'string' && feedback.next.label.trim() ? feedback.next : null;
  const when = next ? nextWhen(next) : '';
  const upcoming = next && Array.isArray(next.upcoming) ? next.upcoming.filter((u) => u && typeof u.label === 'string' && u.label.trim()).slice(0, 8) : [];
  // 🎨 10-02 layout pass (Nina): the heading leads, centered: the night's summary, else the event's name.
  const name = typeof feedback.label === 'string' && feedback.label.trim() ? feedback.label.trim() : '';
  const heading = summary || (name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}` : '');
  const headStyle = { textAlign: 'center', fontSize: '0.95em', fontWeight: 800, padding: '0 18px', overflowWrap: 'anywhere' };
  const closeX = (disabled) => onDismiss && (
    <button type="button" aria-label="Not now" title="Not now" onClick={onDismiss} disabled={disabled}
      style={{ position: 'absolute', top: 8, right: 10, background: 'transparent', border: 'none', cursor: 'pointer', color: muted, fontSize: '0.85em', padding: '0 2px', lineHeight: 1 }}>
      ✕
    </button>
  );

  if (state === 'thanks') {
    return (
      <div data-event-feedback="thanks" data-feedback-event={feedback.event_id} style={root}>
        {closeX(false)}
        <div data-feedback-thanks="1" style={headStyle}>{FEEDBACK_THANKS}</div>
        {postcard}
        {next && (
          <div data-feedback-next="1" style={{ marginTop: postcard ? 0 : 8, overflowWrap: 'anywhere' }}>
            <div style={{ fontSize: '0.85em', fontWeight: 700 }}>Next up: {next.emoji ? `${next.emoji} ` : ''}{next.label}</div>
            {when && <div data-feedback-next-when="1" style={{ fontSize: '0.8em', fontWeight: 600, marginTop: 2 }}>{when}</div>}
            {next.line && <div style={{ fontSize: '0.8em', color: muted, marginTop: 2 }}>{next.line}</div>}
            {upcoming.length > 0 && (
              <div data-feedback-upcoming="1" style={{ marginTop: 16 }}>
                <div style={{ fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.1em', color: accent }}>THIS MONTH</div>
                {upcoming.map((u, i) => {
                  const w = nextWhen(u);
                  return <div key={`${u.label}-${i}`} style={{ fontSize: '0.78em', color: muted, marginTop: 3 }}>{u.emoji ? `${u.emoji} ` : ''}{u.label}{w && <span style={{ whiteSpace: 'nowrap' }}>{` · ${w}`}</span>}</div>;
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (photoOnly) {
    // 📸 10-06: the event's last 30 minutes = its group photo (the heading, the photo + Share, ✕); the review comes after.
    return (
      <div data-event-feedback="photo" data-feedback-stage="photo" data-feedback-event={feedback.event_id} style={root}>
        <style>{OPT_CSS}</style>
        {closeX(false)}
        {heading && <div data-feedback-heading="1" {...(summary ? { 'data-feedback-summary': '1' } : {})} style={{ ...headStyle, marginBottom: 0 }}>{heading}</div>}
        {postcard}
      </div>
    );
  }

  return (
    <div data-event-feedback={sending ? 'sending' : 'idle'} data-feedback-event={feedback.event_id} style={root}>
      <style>{OPT_CSS}</style>
      {closeX(sending)}
      {heading && <div data-feedback-heading="1" {...(summary ? { 'data-feedback-summary': '1' } : {})} style={{ ...headStyle, marginBottom: postcard ? 0 : 10 }}>{heading}</div>}
      {postcard}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {questions.map((q) => (
          <div key={q.key} data-feedback-q={q.key} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ fontSize: '0.8em', color: muted, overflowWrap: 'anywhere' }}>{q.prompt || q.q}</span>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {q.options.map((label, index) => {
                const on = a[q.key] === index;
                return (
                  <button key={`${q.key}-${index}`} type="button" className="event-fb-opt" data-feedback-opt={index} aria-pressed={on} disabled={sending}
                    onClick={() => onAnswer && onAnswer(q.key, index)}
                    style={{
                      font: 'inherit', fontSize: '0.82em', fontWeight: on ? 800 : 600, padding: '5px 10px', borderRadius: 999, cursor: 'pointer', boxSizing: 'border-box',
                      background: on ? `${accent}44` : 'rgba(255,255,255,0.04)', border: `1px solid ${on ? accent : `${accent}40`}`, color: ink,
                      overflowWrap: 'anywhere',
                    }}>
                    {String(label)}
                  </button>
                );
              })}
            </span>
          </div>
        ))}
      </div>
      <input
        type="text"
        data-feedback-text="1"
        value={text || ''}
        maxLength={FEEDBACK_TEXT_MAX}
        placeholder={FEEDBACK_PLACEHOLDER}
        aria-label={FEEDBACK_PLACEHOLDER}
        autoComplete="off"
        disabled={sending}
        onChange={(e) => onText && onText(String(e.target.value || '').slice(0, FEEDBACK_TEXT_MAX))}
        onKeyDown={(e) => { if (e.key === 'Enter' && canSend && onSend) { e.preventDefault(); onSend(); } }}
        style={{
          font: 'inherit', fontSize: 16, width: '100%', boxSizing: 'border-box', marginTop: 9,
          padding: '7px 10px', borderRadius: 8, border: `1px solid ${accent}40`, background: 'rgba(255,255,255,0.04)', color: ink,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button type="button" data-feedback-skip="1" onClick={onSkip} disabled={sending}
          style={{ font: 'inherit', fontSize: '0.82em', fontWeight: 700, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${accent}40`, color: muted }}>
          Skip
        </button>
        <button type="button" data-feedback-send="1" onClick={() => { if (canSend && onSend) onSend(); }} disabled={!canSend}
          style={{ font: 'inherit', fontSize: '0.82em', fontWeight: 800, padding: '6px 12px', borderRadius: 8, cursor: canSend ? 'pointer' : 'default', background: canSend ? accent : `${accent}33`, border: `1px solid ${accent}`, color: '#fff', opacity: canSend ? 1 : 0.6 }}>
          {sending ? 'Sending' : 'Send'}
        </button>
      </div>
    </div>
  );
}
